<template>
  <v-app>
    <v-container fluid>
      <v-row v-if="$insertAccess($route.fullPath)">
        <v-col cols="12">
          <!-- Attendance Entry Form -->
          <v-expansion-panels v-model="panel2" multiple>
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Attendance Manual Entry
              </v-expansion-panel-title>
              <v-expansion-panel-text class="pa-3">
                <v-form @submit.prevent="submit" ref="attendanceForm" id="attendanceForm">
                  <v-card variant="outlined" class="mb-3">
                    <v-card-title class="text-subtitle-1 bg-grey-lighten-4 pa-3">
                      <v-icon class="me-2" size="small">mdi-clock-time-four-outline</v-icon>
                      Attendance Entries
                    </v-card-title>
                    
                    <v-card-text class="pa-4">
                      <!-- Attendance Entry Rows -->
                      <div v-for="(entry, index) in attendanceEntries" :key="index" class="mb-4">
                        <v-row align="center" class="attendance-row" dense>
                          <!-- Employee -->
                          <v-col cols="12" sm="6" md="4" lg="3">
                            <v-autocomplete
                              variant="outlined"
                              density="compact"
                              bg-color="grey-lighten-4"
                              v-model="entry.employee_id"
                              :items="employees"
                              :item-title="(item) => `${item.name} - ${item.code || item.employee_code || item.emp_id || ''}`"
                              item-value="id"
                              :loading="employeesLoading"
                              autocomplete="off"
                              clearable
                              hide-details="auto"
                              no-data-text="No employees found"
                              placeholder="Select employee"
                            >
                              <template #label>
                                Employee
                                <span class="text-red-accent-4">*</span>
                              </template>
                            </v-autocomplete>
                          </v-col>

                          <!-- Date -->
                          <v-col cols="12" sm="4" md="2" lg="2">
                            <v-date-input
                              input-format="dd/mm/yyyy"
                              variant="outlined"
                              density="compact"
                              bg-color="grey-lighten-4"
                              v-model="entry.attendance_date"
                              prepend-icon=""
                              prepend-inner-icon="mdi-calendar"
                              autocomplete="off"
                              hide-details="auto"
                              clearable
                            >
                              <template #label>
                                Date
                                <span class="text-red-accent-4">*</span>
                              </template>
                            </v-date-input>
                          </v-col>

                          <!-- In Time -->
                          <v-col cols="12" sm="4" md="2" lg="2">
                            <v-text-field
                              :model-value="getInTimeDisplay(entry.punch_in_time_24)"
                              label="In Time"
                              readonly
                              variant="outlined"
                              density="compact"
                              bg-color="grey-lighten-4"
                              prepend-inner-icon="mdi-clock-time-four-outline"
                              @click="openInTimeDialog(index)"
                              clearable
                              hide-details="auto"
                              @click:clear="entry.punch_in_time_24 = ''; entry.punch_in_time = ''"
                            />
                          </v-col>

                          <!-- Out Time -->
                          <v-col cols="12" sm="4" md="2" lg="2">
                            <v-text-field
                              :model-value="getOutTimeDisplay(entry.punch_out_time_24)"
                              label="Out Time"
                              readonly
                              variant="outlined"
                              density="compact"
                              bg-color="grey-lighten-4"
                              prepend-inner-icon="mdi-clock-time-four-outline"
                              @click="openOutTimeDialog(index)"
                              clearable
                              hide-details="auto"
                              @click:clear="entry.punch_out_time_24 = ''; entry.punch_out_time = ''"
                            />
                          </v-col>

                          <!-- Remarks -->
                          <v-col cols="12" sm="4" md="2" lg="2">
                            <v-text-field
                              v-model="entry.remarks"
                              label="Remarks"
                              variant="outlined"
                              density="compact"
                              bg-color="grey-lighten-4"
                              clearable
                              hide-details="auto"
                            />
                          </v-col>

                          <!-- Remove Button -->
                          <v-col cols="12" sm="6" md="1" lg="1" class="d-flex align-center justify-center">
                            <v-btn
                              v-if="attendanceEntries.length > 1"
                              icon="mdi-delete"
                              size="small"
                              variant="text"
                              color="error"
                              @click="removeEntry(index)"
                            ></v-btn>
                          </v-col>
                        </v-row>
                      </div>

                      <!-- Add Button -->
                      <v-row>
                        <v-col cols="2">
                          <v-btn
                            color="primary"
                            variant="flat"
                            prepend-icon="mdi-plus"
                            @click="addEntry"
                            block
                          >
                            Add More
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>

                  <!-- Time Picker Modals -->
                  <v-dialog v-model="inTimeDialog.show" max-width="400">
                    <v-card>
                      <v-card-title class="text-h6">
                        Select Punch In Time
                      </v-card-title>
                      <v-card-text>
                        <v-time-picker
                          v-model="inTimeDialog.time"
                          full-width
                          format="ampm"
                          color="primary"
                          @update:model-value="updateInTime"
                        />
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn
                          color="primary"
                          variant="flat"
                          @click="inTimeDialog.show = false"
                        >
                          OK
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>

                  <v-dialog v-model="outTimeDialog.show" max-width="400">
                    <v-card>
                      <v-card-title class="text-h6">
                        Select Punch Out Time
                      </v-card-title>
                      <v-card-text>
                        <v-time-picker
                          v-model="outTimeDialog.time"
                          full-width
                          format="ampm"
                          color="primary"
                          @update:model-value="updateOutTime"
                        />
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn
                          color="primary"
                          variant="flat"
                          @click="outTimeDialog.show = false"
                        >
                          OK
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>

                  <!-- Action Buttons -->
                  <v-row class="mt-4">
                    <v-col cols="12" class="d-flex justify-end align-center">
                      <v-btn 
                        type="submit"
                        color="success" 
                        variant="flat" 
                        :loading="loading"
                        :disabled="loading || attendanceEntries.length === 0"
                        prepend-icon="mdi-content-save"
                      >
                        SAVE ({{ attendanceEntries.length }})
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import formattedDate from '@/plugins/dateFormatter'

// Store and Router
const store = useStore()
const router = useRouter()

// Panels
const panel2 = ref([0])

// Loading state
const loading = ref(false)
const employeesLoading = ref(false)

// Form validation
const attendanceForm = ref(null)

// Attendance entries array
const attendanceEntries = ref([
  {
    employee_id: null,
    attendance_date: '',
    punch_in_time: '',
    punch_in_time_24: '',
    punch_out_time: '',
    punch_out_time_24: '',
    remarks: ''
  }
])

// Dialog states
const inTimeDialog = ref({
  show: false,
  index: -1,
  time: ''
})

const outTimeDialog = ref({
  show: false,
  index: -1,
  time: ''
})


// Format time for display (24-hour to 12-hour with AM/PM)
const formatTimeForDisplay = (time24) => {
  if (!time24) return ''
  
  try {
    // Handle different time formats
    let hours, minutes
    
    if (typeof time24 === 'string') {
      // Check if it's already in HH:mm format
      if (/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time24)) {
        [hours, minutes] = time24.split(':').map(Number)
      } else {
        // Try to parse as Date or other format
        return time24
      }
    } else {
      return time24
    }
    
    const period = hours >= 12 ? 'PM' : 'AM'
    const hours12 = hours % 12 || 12
    const minutesStr = String(minutes).padStart(2, '0')
    
    return `${hours12}:${minutesStr} ${period}`
  } catch (error) {
    return time24
  }
}

// Helper methods for time display
const getInTimeDisplay = (time24) => {
  return formatTimeForDisplay(time24)
}

const getOutTimeDisplay = (time24) => {
  return formatTimeForDisplay(time24)
}

// Methods for managing entries
const addEntry = () => {
  attendanceEntries.value.push({
    employee_id: null,
    attendance_date: '',
    punch_in_time: '',
    punch_in_time_24: '',
    punch_out_time: '',
    punch_out_time_24: '',
    remarks: ''
  })
}

const removeEntry = (index) => {
  if (attendanceEntries.value.length > 1) {
    attendanceEntries.value.splice(index, 1)
  }
}

// Methods for time dialogs
const openInTimeDialog = (index) => {
  inTimeDialog.value = {
    show: true,
    index: index,
    time: attendanceEntries.value[index].punch_in_time_24 || ''
  }
}

const openOutTimeDialog = (index) => {
  outTimeDialog.value = {
    show: true,
    index: index,
    time: attendanceEntries.value[index].punch_out_time_24 || ''
  }
}

const updateInTime = (time) => {
  if (inTimeDialog.value.index >= 0) {
    attendanceEntries.value[inTimeDialog.value.index].punch_in_time_24 = time
    attendanceEntries.value[inTimeDialog.value.index].punch_in_time = time
  }
}

const updateOutTime = (time) => {
  if (outTimeDialog.value.index >= 0) {
    attendanceEntries.value[outTimeDialog.value.index].punch_out_time_24 = time
    attendanceEntries.value[outTimeDialog.value.index].punch_out_time = time
  }
}

// Dropdown options
const employees = ref([])

// Methods
const loadEmployees = async () => {
  employeesLoading.value = true
  try {
    const response = await store.dispatch('getEmployees')
    
    if (response.data.success && response.data.data) {
      employees.value = response.data.data
    } else if (response.data.data) {
      employees.value = response.data.data
    } else {
      employees.value = []
      toast.error('No employee data found in response')
    }
  } catch (error) {
    employees.value = []
    toast.error(`Failed to load employees: ${error.message || 'Unknown error'}`)
  } finally {
    employeesLoading.value = false
  }
}

const formatTimeForAPI = (timeValue) => {
  if (!timeValue) return null

  // Debug: Log input
  console.log('formatTimeForAPI input:', timeValue, 'type:', typeof timeValue)

  try {
    // Handle Date object (what v-time-picker returns)
    if (timeValue instanceof Date) {
      const hours = String(timeValue.getHours()).padStart(2, '0')
      const minutes = String(timeValue.getMinutes()).padStart(2, '0')
      const result = `${hours}:${minutes}`
      console.log('formatTimeForAPI output (Date):', result)
      return result
    }
    
    // Handle ISO timestamp strings (e.g., "2026-01-09T18:00:00.000Z")
    if (typeof timeValue === 'string' && timeValue.includes('T') && timeValue.includes('Z')) {
      const date = new Date(timeValue)
      if (!isNaN(date.getTime())) {
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const result = `${hours}:${minutes}`
        console.log('formatTimeForAPI output (ISO with Z):', result)
        return result
      }
    }
    
    // Handle ISO timestamp without Z (e.g., "2026-01-09T18:00:00.000")
    if (typeof timeValue === 'string' && timeValue.includes('T') && !timeValue.includes('Z')) {
      const date = new Date(timeValue)
      if (!isNaN(date.getTime())) {
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const result = `${hours}:${minutes}`
        console.log('formatTimeForAPI output (ISO without Z):', result)
        return result
      }
    }
    
    // Check if it's already in 24-hour format (HH:mm)
    if (typeof timeValue === 'string' && /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeValue)) {
      console.log('formatTimeForAPI output (already HH:mm):', timeValue)
      return timeValue
    }
    
    // If it's in 12-hour format with AM/PM, convert it
    if (typeof timeValue === 'string') {
      const time12hr = timeValue.trim().toUpperCase()
      const isPM = time12hr.includes('PM')
      const isAM = time12hr.includes('AM')
      
      if (isPM || isAM) {
        // Extract hours and minutes
        const timePart = time12hr.replace(/\s*(AM|PM)\s*/i, '')
        const [hours, minutes] = timePart.split(':')
        let hour24 = parseInt(hours, 10)
        
        if (isPM && hour24 !== 12) {
          hour24 += 12
        } else if (isAM && hour24 === 12) {
          hour24 = 0
        }
        
        const result = `${String(hour24).padStart(2, '0')}:${minutes || '00'}`
        console.log('formatTimeForAPI output (12hr converted):', result)
        return result
      }
    }
    
    // If format is unexpected, return null for safety
    console.warn('Unexpected time format:', timeValue)
    return null
  } catch (error) {
    console.error('Error formatting time:', error, 'Input:', timeValue)
    return null
  }
}

const submit = async () => {
  loading.value = true
  
  try {
    // Validate entries
    if (attendanceEntries.value.length === 0) {
      toast.error('Please add at least one attendance entry')
      loading.value = false
      return
    }

    // Validate each entry
    const employeeIds = []
    const attendanceDates = []
    const punchInTimes = []
    const punchOutTimes = []
    const remarksArray = []
    
    for (let i = 0; i < attendanceEntries.value.length; i++) {
      const entry = attendanceEntries.value[i]
      
      if (!entry.employee_id) {
        toast.error(`Please select an employee for entry ${i + 1}`)
        loading.value = false
        return
      }
      
      if (!entry.attendance_date) {
        toast.error(`Please select a date for entry ${i + 1}`)
        loading.value = false
        return
      }
      
      // At least one time must be provided
      if (!entry.punch_in_time && !entry.punch_out_time) {
        toast.error(`Please provide at least In Time or Out Time for entry ${i + 1}`)
        loading.value = false
        return
      }

      // Format date for API (YYYY-MM-DD) - use centralized formatter
      const formattedDateValue = formattedDate(entry.attendance_date)

      // Add entry data to arrays
      employeeIds.push(entry.employee_id)
      attendanceDates.push(formattedDateValue)
      punchInTimes.push(formatTimeForAPI(entry.punch_in_time) || null)
      punchOutTimes.push(formatTimeForAPI(entry.punch_out_time) || null)
      // Ensure remarks is always a string, not null or undefined
      remarksArray.push(String(entry.remarks || ''))
    }

    // Prepare payload in the required format (object with arrays)
    const payload = {
      employee_id: employeeIds,
      attendance_date: attendanceDates,
      punch_in_time: punchInTimes,
      punch_out_time: punchOutTimes,
      remarks: remarksArray
    }

    // Debug: Log payload to verify formatting
    console.log('Formatted date sample:', formattedDate(new Date()))
    console.log('Payload being sent:', payload)

    // Submit all attendance entries
    const response = await store.dispatch('saveBulkManualAttendance', payload)
    
    if (response.success) {
      toast.success(`Manual attendance saved successfully for ${employeeIds.length} entry/entries!`)
      resetForm()
    } else {
      toast.error(response.message || 'Failed to save manual attendance entries')
    }
    
  } catch (error) {
    toast.error(error.response?.data?.message || error.message || 'Failed to save manual attendance')
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  attendanceEntries.value = [
    {
      employee_id: null,
      attendance_date: '',
      punch_in_time: '',
      punch_in_time_24: '',
      punch_out_time: '',
      punch_out_time_24: '',
      remarks: ''
    }
  ]
}


// Initialize
onMounted(async () => {
  await loadEmployees()
})
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

/* Standard spacing */
.v-expansion-panels {
  margin-bottom: 16px;
}

.v-expansion-panel-text__wrapper {
  padding: 0;
}

/* Attendance row styling */
.attendance-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  padding-bottom: 8px;
}

.attendance-row:last-child {
  border-bottom: none;
}
</style>

